/**
 * ServerOfflineBanner — global warning strip shown on every V2 page while an
 * onsite server is reported offline.
 *
 * The alert itself is an ordinary 5.0 alarm (`event_type_id` 41,
 * `event_category_id` 19) arriving on the shared subscription, so it already lands
 * on the alerts page like anything else. This banner exists because that page is
 * the one place staff might NOT be: an offline server means alerts may not be
 * reaching anyone at all, which is exactly the moment nobody should have to be
 * looking at the right screen to find out.
 *
 * Mounted once in `App.jsx`, immediately BEFORE `<Routes>` — inside `BrowserRouter`
 * but outside `<Routes>`, so navigating never unmounts it and it needs no per-page
 * wiring. Same self-gating contract as the `<HelpGuide />` below it: this renders
 * `null` whenever it should not be seen. The position before `<Routes>` is load
 * bearing, not stylistic — see (2).
 *
 * Four things here are deliberate:
 *
 *  1. **No dismiss.** It clears itself when the alert clears. While the server is
 *     down there is no state of the world in which hiding this is the right call.
 *
 *  2. **It sits in normal flow, so it cannot cover the page.** `position: sticky`
 *     and not `fixed`: sticky occupies real layout space, so `.nav2-main` — the
 *     only in-flow element after it, since the sidebar and the mobile controls are
 *     all fixed — is pushed down by exactly the banner's height, at every width and
 *     for any number of offline servers, with nothing measured and nothing to keep
 *     in sync. It still pins to the top on scroll like a fixed strip would.
 *
 *     This started as `fixed` with the height hard-coded in CSS and the layout
 *     padded to match. That was 30px short at 375px wide and 20px short with three
 *     servers listed, both of which silently hid the top of the page; measuring the
 *     height fixed it but replaced a layout constant with a ResizeObserver, a CSS
 *     custom property and a root attribute — three moving parts to get exactly
 *     what flow gives for free. Hence: **mounted before `<Routes>` in App.jsx**, so
 *     DOM order puts it above the routed page.
 *
 *  3. **V2 only.** V1 is being retired and lays its shell out differently, so the
 *     flow assumption in (2) does not hold there. Gating here rather than touching
 *     V1 markup keeps this additive.
 *
 *  4. **The server link is role-gated at weight 80.** `Community.jsx` renders
 *     "404 Page not found" for a server profile below that, and caregivers (10)
 *     are the main audience for an offline banner. Below 80 the server is named as
 *     plain text — the information without a dead end.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useNotifications } from '../../contexts/NotificationContext.jsx';
import { usePortalVersion } from '../../contexts/PortalVersionContext.jsx';
import { serverOfflineAlerts, utcToMs, formatSpanShort } from '../../utils/activeAlerts.js';
import { getRoleWeight, getCampusTzOffset } from '../../services/session.js';
import { iAmHigherAuthority, convertIsoUTCToLocal } from '../../libs.jsx';
import './server-offline-banner.css';

/* Weight required to open a server profile — mirrors Community.jsx's own gate. */
const SERVER_PROFILE_WEIGHT = 80;

/* One second, matching the `useTick(1000)` the alerts tables already use for their
   own duration columns.

   It has to be one second rather than something slower because `formatSpanShort`
   shows SECONDS below a minute: on a 60s tick a fresh alert sat on "20s" for a full
   minute and then jumped to "1m 20s", which reads as a frozen readout rather than a
   live one. Whatever the smallest displayed unit is, the tick has to match it.

   The cost is one small component re-rendering, not the app: this is mounted as a
   sibling of <Routes>, so its state never re-renders any page. */
const TICK_MS = 1000;

const ServerOfflineBanner = () => {
  const { activeCloudAlarms, activeAlarms } = useNotifications();
  const { portalVersion } = usePortalVersion();

  const offline = useMemo(
    () => serverOfflineAlerts(activeCloudAlarms, activeAlarms),
    [activeCloudAlarms, activeAlarms],
  );

  const isV2 = portalVersion === 'v2';
  const show = isV2 && offline.length > 0;

  /* Ticks only while the banner is up, so an app with no offline server keeps no
     timer at all. */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!show) return undefined;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, [show]);

  /* After the hooks, never before — `show` flips at runtime when an alert arrives,
     and an early return above the effect would change the hook count between
     renders and take the whole app subtree down with it. */
  if (!show) return null;

  const canOpenServer = iAmHigherAuthority(SERVER_PROFILE_WEIGHT, getRoleWeight());
  const multiple = offline.length > 1;
  /* Campus-local, the same conversion every alert timestamp in the app goes
     through — a raw UTC string here would read an hour or more off for the person
     standing in the building. */
  const tzOffset = getCampusTzOffset();

  const sinceLabel = (alert) => {
    if (!alert.startedUtc) return '';
    const started = utcToMs(alert.startedUtc);
    const forPart = started == null ? '' : ` (${formatSpanShort(now - started)})`;
    return `${convertIsoUTCToLocal(alert.startedUtc, tzOffset) || ''}${forPart}`;
  };

  return (
    <div className="sob-banner" role="alert" aria-live="assertive">
      <span className="sob-banner__icon" aria-hidden="true">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="2" width="20" height="8" rx="2" />
          <rect x="2" y="14" width="20" height="8" rx="2" />
          <line x1="6" y1="6" x2="6.01" y2="6" />
          <line x1="6" y1="18" x2="6.01" y2="18" />
        </svg>
      </span>

      <div className="sob-banner__body">
        <span className="sob-banner__title">
          {multiple
            ? `${offline.length} onsite servers are offline`
            : 'Your onsite server appears offline'}
        </span>
        <span className="sob-banner__detail">
          {multiple
            ? 'Alerts from these servers may not be reaching caregivers. For assistance contact PalCare support at (888) 725-2848.'
            : 'Alerts may not be reaching caregivers. For assistance contact PalCare support at (888) 725-2848.'}
        </span>
      </div>

      <ul className="sob-banner__list">
        {offline.map((alert) => (
          <li className="sob-banner__item" key={alert.key}>
            {canOpenServer && alert.deviceId ? (
              <Link className="sob-banner__link" to={`/community/server_devices/${alert.deviceId}`}>
                {alert.deviceName || `Server ${alert.deviceId}`}
              </Link>
            ) : (
              <span className="sob-banner__name">
                {alert.deviceName || (alert.deviceId ? `Server ${alert.deviceId}` : 'Onsite server')}
              </span>
            )}
            {alert.startedUtc ? (
              <span className="sob-banner__since">{` offline since ${sinceLabel(alert)}`}</span>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ServerOfflineBanner;
